// Authentication service for Google Workspace integration
import { api, endpoints } from './api';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
  isAdmin: boolean;
  tenantId?: string;
  identityProvider: string;
  roles: string[];
  // Clio-specific fields
  clioOrgId?: string;
  clioOrgName?: string;
  clioOrgAlias?: string;
}

export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface SocialLoginUrlResponse {
  loginUrl: string;
  provider: string;
  tenantId: string;
}

export interface SocialLoginParams {
  tenantId?: string;
}

export interface OAuthCallbackParams {
  code: string;
  state: string;
}

export interface OAuthCallbackResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiredAt: number;
  user: {
    id: string;
    email: string;
    name: string;
    picture?: string;
  };
}

class AuthService {
  // Get the correct redirect URI for admin app
  private getRedirectUri(): string {
    const baseUrl = window.location.origin; // This will be http://localhost:3001 in dev
    return `${baseUrl}/auth/callback`;
  }

  // Get social login URL
  async getSocialLoginUrl({ tenantId = 'auto' }: SocialLoginParams): Promise<SocialLoginUrlResponse> {
    const redirectUri = this.getRedirectUri();
    const params = new URLSearchParams({
      tenantId,
      redirectUri
    });

    return api.get<SocialLoginUrlResponse>(`${endpoints.auth.adminLoginUrl}?${params.toString()}`, undefined, true);
  }

  // Handle OAuth callback
  async handleOAuthCallback({ code, state }: OAuthCallbackParams): Promise<OAuthCallbackResponse> {
    try {
      const redirectUri = this.getRedirectUri();
      const params = new URLSearchParams({
        code,
        state,
        redirectUri
      });

      const response = await api.post<OAuthCallbackResponse>(
        `${endpoints.auth.adminCallback}?${params.toString()}`,
        null,
        undefined,
        true
      );

      // Store tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('idToken', response.idToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('expiredAt', `${response.expiredAt}`);

      try {
        const userRes = await api.get<AuthUser>(`${endpoints.auth.me}`);
        localStorage.setItem('user', JSON.stringify(userRes));
      } catch (error) {
        this.clearAuthData();
        throw error;
      }

      return response;
    } catch (error: any) {
      // Check if this is an unauthorized domain error
      if (error?.response?.status === 401 ||
        (error?.message && error.message.includes('Access denied for domain'))) {

        // Clear any stored tokens
        this.clearAuthData();

        // Redirect to XTND.legal with error message
        const errorMessage = encodeURIComponent(
          error?.message || 'Your domain is not authorized to access this application. Please contact support.'
        );
        window.location.href = `https://xtnd.legal/?error=unauthorized_domain&message=${errorMessage}`;

        // Throw error to prevent further processing
        throw new Error('Unauthorized domain - redirecting to xtnd.legal');
      }

      // Re-throw other errors
      throw error;
    }
  }

  // Clear authentication data
  private clearAuthData(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiredAt');
    localStorage.removeItem('user');
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    // Check if JWT authentication is disabled (for local development)
    if ((import.meta as any).env?.VITE_DISABLE_JWT_AUTH === 'true') {
      return true;
    }

    const accessToken = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');
    return !!(accessToken && user);
  }

  // Get current user from localStorage
  getCurrentUser(): AuthUser | null {
    // Return mock user when authentication is disabled (for local development)
    if ((import.meta as any).env?.VITE_DISABLE_JWT_AUTH === 'true') {
      return {
        id: 'dev-user-123',
        email: 'ben.elliott@thecoraledge.com',
        name: 'Ben Elliott (Dev)',
        picture: undefined,
        isAdmin: true,
        tenantId: '0000000001',
        identityProvider: 'dev',
        roles: ['admin', 'user'],
      };
    }

    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;

      const userData = JSON.parse(userStr);

      // Parse roles from token claims
      const roles = [
        ...(userData['cognito:groups'] || []),
        ...(userData['custom:roles']?.split(',') || [])
      ].filter(Boolean);

      // Determine admin status from roles
      const isAdmin = roles.some(role =>
        role === 'admin' ||
        role === 'clio_admin' ||
        role.startsWith('admin:')
      );

      return {
        id: userData.id,
        email: userData.email,
        name: userData.name || `${userData.given_name} ${userData.family_name}`.trim(),
        picture: userData.picture,
        isAdmin,
        tenantId: userData['custom:tenantId'] || userData.tenantId,
        identityProvider: userData['custom:identityProvider'] || 'cognito',
        roles,
        // Clio-specific fields
        clioOrgId: userData['custom:clioOrgId'],
        clioOrgName: userData['custom:clioOrgName'],
        clioOrgAlias: userData['custom:clioOrgAlias'],
      };
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      // Clear tokens and user data
      this.clearAuthData();
    } catch (error) {
      console.error('Sign out failed:', error);
      throw new Error('Failed to sign out');
    }
  }

  // Get access token
  async getAccessToken(): Promise<string | null> {
    const expiredAt = localStorage.getItem('expiredAt');
    if (expiredAt && Date.now() > parseInt(expiredAt)) {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('Access token expired and no refresh token available');
      }

      const response = await api.post<OAuthCallbackResponse>(
        `${endpoints.auth.refresh}?refreshToken=${refreshToken}`,
        null, undefined, true
      );
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('idToken', response.idToken);
      localStorage.setItem('expiredAt', `${response.expiredAt}`);
    }

    return localStorage.getItem('idToken');
  }
}

// Export singleton instance
export const authService = new AuthService();
